import { isAuthStatus } from './auth';
import { timeClientAsync } from './timing';

export type SymbolAssetType = 'etf' | 'index' | 'stock';

export interface SupportedSymbol {
  ticker: string;
  label: string;
  assetType: SymbolAssetType;
  enabled: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface SymbolUniverse {
  symbols: SupportedSymbol[];
  tickerUniverseExpansionEnabled: boolean;
  rightRailDashboardEnabled?: boolean;
}

interface SymbolsApiResponse {
  success: boolean;
  data?: SymbolUniverse;
  error?: string;
}

interface SymbolApiResponse {
  success: boolean;
  data?: SupportedSymbol;
  error?: string;
}

export class SymbolUniverseRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'SymbolUniverseRequestError';
  }
}

export interface SymbolCreateInput {
  ticker: string;
  label: string;
  assetType: SymbolAssetType;
  enabled: boolean;
  sortOrder: number;
}

export interface SymbolUpdateInput {
  label?: string;
  assetType?: SymbolAssetType;
  enabled?: boolean;
  sortOrder?: number;
}

export function isAdminAccessLevel(accessLevel: string | null | undefined): boolean {
  return accessLevel === 'admin' || accessLevel === 'owner' || accessLevel === 'seller';
}

export async function fetchSymbolUniverse(): Promise<SymbolUniverse> {
  return timeClientAsync('ui/symbols-fetch', async () => {
    const response = await fetch('/api/symbols');
    const json = await readSymbolsResponse(response);

    if (!response.ok || !json.success || json.data == null) {
      throw new SymbolUniverseRequestError(json.error ?? 'Failed to load symbols', response.status);
    }

    return json.data;
  });
}

async function readSymbolsResponse(response: Response): Promise<SymbolsApiResponse> {
  try {
    return (await response.json()) as SymbolsApiResponse;
  } catch {
    throw new SymbolUniverseRequestError('Failed to load symbols', response.status);
  }
}

export async function fetchEnabledSymbols(): Promise<SupportedSymbol[]> {
  return (await fetchSymbolUniverse()).symbols;
}

export async function fetchAdminSymbols(): Promise<SupportedSymbol[]> {
  const response = await fetch('/api/admin/symbols');
  const json = (await response.json()) as SymbolsApiResponse;

  if (!response.ok || !json.success || json.data == null) {
    if (isAuthStatus(response.status)) {
      throw new Error(json.error ?? 'Admin access required');
    }
    throw new Error(json.error ?? 'Failed to load admin symbols');
  }

  return json.data.symbols;
}

export async function createAdminSymbol(input: SymbolCreateInput): Promise<SupportedSymbol> {
  const response = await fetch('/api/admin/symbols', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return readSymbolResponse(response, 'Failed to create symbol');
}

export async function updateAdminSymbol(
  ticker: string,
  input: SymbolUpdateInput,
): Promise<SupportedSymbol> {
  const response = await fetch(`/api/admin/symbols/${encodeURIComponent(ticker)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return readSymbolResponse(response, 'Failed to update symbol');
}

async function readSymbolResponse(
  response: Response,
  fallbackError: string,
): Promise<SupportedSymbol> {
  const json = (await response.json()) as SymbolApiResponse;

  if (!response.ok || !json.success || json.data == null) {
    throw new Error(json.error ?? fallbackError);
  }

  return json.data;
}

export interface AvailableSymbol {
  ticker: string;
  name: string;
  type: string | null;
  primaryExchange: string | null;
  supported: boolean;
}

interface AvailableSymbolsApiResponse {
  success: boolean;
  data?: { results: AvailableSymbol[] };
  error?: string;
}

export async function searchAvailableSymbols(search: string): Promise<AvailableSymbol[]> {
  const response = await fetch(`/api/admin/symbols/available?search=${encodeURIComponent(search)}`);
  const json = (await response.json()) as AvailableSymbolsApiResponse;

  if (!response.ok || !json.success || json.data == null) {
    throw new Error(json.error ?? 'Massive ticker search failed');
  }

  return json.data.results;
}

export async function reorderAdminSymbols(tickers: string[]): Promise<SupportedSymbol[]> {
  const response = await fetch('/api/admin/symbols/order', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tickers }),
  });
  const json = (await response.json()) as SymbolsApiResponse;

  if (!response.ok || !json.success || json.data == null) {
    throw new Error(json.error ?? 'Failed to save symbol order');
  }

  return json.data.symbols;
}
